<script setup lang="ts">
import {
  faBriefcase,
  faChevronDown,
  faFileInvoiceDollar,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { GpBtn, GpCollapse, GpCollapsePanel, GpTabBar } from '@gportal/uikit';
import { type ServiceError } from 'gpcore-apikit/grpc/types';
import { useUserStore } from 'gpcore-apikit/store/pinia/user';
import { type BillingProfile } from 'gpcore-grpc/gpcore/api/cloud/v1/billing_pb';
import ConfirmModal from 'gpcore-uikit/components/new/ConfirmModal.vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { provide, ref, toRef } from 'vue';
import AddressField from './address/AddressField.vue';
import { BillingProfileInject } from './symbols';
import BillsTable from './table-bills/BillsTable.vue';
import ProjectsTable from './table-projects/ProjectsTable.vue';

const props = defineProps<{
  billingProfile: BillingProfile;
}>();

const emit = defineEmits<{ (e: 'deleted') }>();

const billingProfileRef = toRef(props, 'billingProfile');
provide(BillingProfileInject, billingProfileRef);

const userStore = useUserStore();
const isConfirmModalOpened = ref(false);
const isDeleting = ref(false);
const { addNotification } = useNotifications();
const deleteBillingProfile = async () => {
  isDeleting.value = true;
  try {
    await userStore.deleteBillingProfile(props.billingProfile.id);
    isConfirmModalOpened.value = false;

    addNotification({
      message: 'Billing profile has been deleted.',
      type: 'success',
    });

    emit('deleted');
  } catch (error) {
    addNotification({
      message: (error as ServiceError).message,
      type: 'danger',
    });
  } finally {
    isDeleting.value = false;
  }
};

const tab = ref<'bills' | 'projects'>('bills');
const visiblePanel = ref(null);
const openCollapse = () => {
  visiblePanel.value = billingProfileRef.value.id;
};

// highlight section after creation
const isHighlighting = ref(false);
const highlight = () => {
  isHighlighting.value = true;
  setTimeout(() => {
    isHighlighting.value = false;
  }, 1000);
};

defineExpose({
  openCollapse,
  highlight,
});
</script>

<template>
  <GpCollapse v-model="visiblePanel" outline>
    <GpCollapsePanel
      :panel-key="billingProfile.id"
      body-class="tw-p-4 tw-border-b tw-border-l tw-border-r tw-border-gp-rhino tw-rounded-b"
      style="--collapse-max-height: 1000px"
    >
      <!-- header -->
      <template #header="{ toggle, isOpened, collapseId }">
        <a
          type="button"
          class="tw-flex tw-cursor-pointer tw-flex-wrap tw-items-center tw-border tw-border-gp-rhino tw-bg-transparent tw-px-4 tw-py-2 tw-text-gp-mouse tw-transition-colors hover:tw-bg-gp-midnight-2 hover:tw-text-gp-mouse-light"
          :class="{
            'tw-rounded': !isOpened,
            'tw-rounded-t': isOpened,
            'tw-scale-[1.02]': isHighlighting,
          }"
          :id="collapseId"
          @click.prevent="toggle"
        >
          <FontAwesomeIcon
            :icon="faChevronDown"
            class="tw-mr-4"
            :class="{ 'tw-rotate-180': isOpened }"
          />
          <div class="tw-flex-1">
            <h2>
              <template v-if="billingProfile.company && billingProfile.company.name">
                {{ billingProfile.company.name }} -
              </template>
              {{ billingProfile.name }}
            </h2>
            <small>ID: {{ billingProfile.id }}</small>
          </div>
          <GpBtn
            size="sm"
            btn-type="danger"
            outline
            label="Delete"
            :icon="faTrash"
            @click.stop="isConfirmModalOpened = true"
          />
          <ConfirmModal
            :show="isConfirmModalOpened"
            :question="`Are you sure you want to delete the billing profile '${
              (billingProfile.company && billingProfile.company.name
                ? `${billingProfile.company.name} - `
                : '') + billingProfile.name
            }'?`"
            :confirm-text="isDeleting ? 'Deleting ...' : 'Delete'"
            confirm-type="danger"
            @close="isConfirmModalOpened = false"
            @confirm="deleteBillingProfile"
          />
        </a>
      </template>
      <!-- content -->
      <template #body>
        <div
          class="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-3 md:tw-gap-8 xl:tw-grid-cols-4"
        >
          <div class="tw-col-span-full md:tw-col-span-1">
            <AddressField />
          </div>
          <div class="tw-col-span-full md:tw-col-span-2 xl:tw-col-span-3">
            Currently no credit cards supported
          </div>
          <div class="tw-col-span-full">
            <GpTabBar
              v-model="tab"
              :overlay-breakpoint="null"
              :tabs-model="[
                { id: 'bills', label: 'Bills', icon: faFileInvoiceDollar },
                { id: 'projects', label: 'Projects', icon: faBriefcase },
              ]"
            >
              <template #item="{ item, isActive }">
                <a
                  href="#"
                  data-gp-element="gp-tab-entry"
                  class="tw-mb-0 tw-border-b-2 tw-pb-2 tw-text-2xl tw-font-bold tw-transition-all hover:tw-border-b-gp-cyan hover:tw-text-gp-cyan"
                  :class="{
                    'tw-border-b-gp-cyan tw-text-gp-cyan': isActive,
                    'tw-border-b-gp-rhino tw-text-gp-rhino': !isActive,
                  }"
                  @click.prevent="tab = item.id as 'bills' | 'projects'"
                >
                  {{ item.label }}
                </a>
              </template>
            </GpTabBar>
            <transition name="fade" mode="out-in">
              <BillsTable class="tw-mt-4" v-if="tab === 'bills'" />
              <ProjectsTable class="tw-mt-4" v-else-if="tab === 'projects'" />
            </transition>
          </div>
        </div>
      </template>
    </GpCollapsePanel>
  </GpCollapse>
</template>
